import { Icon } from "@iconify/react";
import { Dialog } from "@mui/material";
import { FormikConfig, FormikProps, useFormik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { SwitchButton } from "../../components/shared/SwitchButton";
import {
  convertFileToBase64,
  downloadBlobObject,
} from "../../utils/DownloadFile";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  SettingHeader,
  SettingsRow,
  VStack,
  isEsopViewer,
} from "../../components/utils";
import {
  AuthMethods,
  EmployeePortalSetting,
  GrantTagCatalogEntry,
  NomineeFormTemplate,
} from "../../types/SiteSettings";
import { getCurrencySymbol } from "../../utils/currencyFormatter";

import {
  useGetEmployeePortalSettings,
  useSetEmployeePortalSettings,
} from "../../queries/siteSettings";
import { useValuationDetails } from "../../queries/Valuation";
import { useGetGrantPPS } from "../../queries/employees";
import { useCompanyStore } from "../../store";
import { usePermissionStore } from "../../store/permissionStore";
import { determineUserAccessToResource } from "../../utils/auth";
import {
  Action as DefaultAction,
  Resource,
} from "../../utils/interfaces/Companies";
import { MultiSelectV2 } from "../../components/shared/MultiSelectV2";
import { Input, Label } from "../../components/shared/InputField";
import Tooltip from "../../components/shared/Tooltip";
import { useIsInternalUser } from "../../utils/useIsInternalUser";

type SubSettingsProps = {
  formik: FormikProps<EmployeePortalSetting>;
};
type SettingsRowProps = {
  label: string;
  description?: string;
  value: boolean;
};

function GrantTagCatalogSettings({ formik }: SubSettingsProps) {
  const emptyTag: GrantTagCatalogEntry = {
    label: "",
    value: "",
    active: true,
    employeeVisible: false,
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState(emptyTag);
  const [validationError, setValidationError] = useState("");
  const catalog = formik.values.grantTagCatalog || [];
  const updateCatalog = (next: GrantTagCatalogEntry[]) =>
    formik.setFieldValue("grantTagCatalog", next);
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingIndex(null);
    setDraft(emptyTag);
    setValidationError("");
  };
  const openModal = (index?: number) => {
    setIsModalOpen(true);
    setValidationError("");
    if (index === undefined) {
      setEditingIndex(null);
      setDraft(emptyTag);
      return;
    }
    setEditingIndex(index);
    setDraft(catalog[index]);
  };
  const saveTag = () => {
    const nextTag = {
      ...draft,
      label: draft.label.trim(),
      value: draft.label.trim(),
    };
    if (!nextTag.label || !nextTag.value) {
      setValidationError("Profile label is required.");
      return;
    }
    if (
      catalog.some(
        (tag, index) => tag.value === nextTag.value && index !== editingIndex
      )
    ) {
      setValidationError("Profile label must be unique.");
      return;
    }
    const nextCatalog = [...catalog];
    if (editingIndex === null) nextCatalog.push(nextTag);
    else nextCatalog[editingIndex] = nextTag;
    updateCatalog(nextCatalog);
    closeModal();
  };

  return (
    <VStack className="gap-4 border-t-2 border-dotted pt-6">
      <HStack className="items-start justify-between gap-4">
        <Box>
          <Label className="text-base font-medium">Grant tag profiles</Label>
          <Box className="mt-1 text-sm text-gray-500">
            {catalog.length} {catalog.length === 1 ? "profile" : "profiles"}.
            Manage reusable grant classifications and employee visibility.
          </Box>
        </Box>
        <ButtonPrimary type="button" onClick={() => openModal()}>
          <HStack className="items-center gap-1">
            <Icon icon="lucide:plus" width={15} /> Add profile
          </HStack>
        </ButtonPrimary>
      </HStack>
      <Box className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full min-w-[600px] text-left text-sm">
          <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3">Label</th>
              <th className="px-4 py-3">Grant availability</th>
              <th className="px-4 py-3">Employee visibility</th>
              <th className="px-4 py-3">
                <span className="sr-only">Edit</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {catalog.map((tag, index) => (
              <tr key={tag.value} className="bg-white">
                <td className="px-4 py-3 font-medium text-gray-800">
                  {tag.label}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-semibold ${
                      tag.active
                        ? "bg-green-50 text-green-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {tag.active ? "Active" : "Archived"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-semibold ${
                      tag.employeeVisible
                        ? "bg-orange-50 text-orange-600"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {tag.employeeVisible ? "Visible" : "Hidden"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <ButtonPrimary1
                    type="button"
                    onClick={() => openModal(index)}
                  >
                    Edit
                  </ButtonPrimary1>
                </td>
              </tr>
            ))}
            {!catalog.length && (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-8 text-center text-sm text-gray-500"
                >
                  No grant tag profiles yet. Add a profile to make it available
                  for grants.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>
      <Box className="text-xs text-gray-500">
        Profile changes are staged here and apply when you save settings.
        Archiving a profile does not remove it from existing grants.
      </Box>
      <Dialog
        open={isModalOpen}
        onClose={closeModal}
        aria-labelledby="grant-tag-profile-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <VStack className="w-full gap-0 bg-white">
          <VStack className="gap-5 px-6 py-5">
            <HStack className="-mx-6 -mt-5 items-start justify-between gap-4 border-b border-borderColor px-6 py-5">
              <Box>
                <h2
                  id="grant-tag-profile-dialog-title"
                  className="text-base leading-6 font-semibold text-headerColor"
                >
                  {editingIndex === null
                    ? "Add grant tag profile"
                    : "Edit grant tag profile"}
                </h2>
                <Box className="mt-1 text-sm leading-5 text-secondaryText">
                  {editingIndex === null
                    ? "Create a reusable profile that admins can assign to grants."
                    : "Update how this profile is named and used."}
                </Box>
              </Box>
              <button
                type="button"
                aria-label="Close grant tag profile dialog"
                onClick={closeModal}
                className="cursor-pointer text-lg font-medium"
              >
                X
              </button>
            </HStack>
            <VStack className="gap-5">
              <Box>
                <Label className="text-sm font-normal">Label</Label>
                <Input
                  className="w-full"
                  value={draft.label}
                  placeholder="Promotion"
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      label: event.target.value,
                      value: event.target.value,
                    })
                  }
                />
              </Box>
              <VStack className="gap-4 rounded-lg border border-gray-200 p-4">
                <HStack className="items-center justify-between">
                  <Box>
                    <Box className="text-sm font-medium">
                      Available for new grants
                    </Box>
                    <Box className="mt-1 text-xs text-gray-500">
                      Archived profiles remain on historic grants but cannot be
                      newly assigned.
                    </Box>
                  </Box>
                  <div>
                    <SwitchButton
                      value={draft.active}
                      label=""
                      className="shrink-0"
                      onClick={() =>
                        setDraft({ ...draft, active: !draft.active })
                      }
                    />
                  </div>
                </HStack>
                <HStack className="items-center justify-between">
                  <Box>
                    <Box className="text-sm font-medium">
                      Visible to employees
                    </Box>
                    <Box className="mt-1 text-xs text-gray-500">
                      Shows this profile on existing employee grants and in
                      grant filters.
                    </Box>
                  </Box>
                  <div>
                    <SwitchButton
                      value={draft.employeeVisible}
                      label=""
                      className="shrink-0"
                      onClick={() =>
                        setDraft({
                          ...draft,
                          employeeVisible: !draft.employeeVisible,
                        })
                      }
                    />
                  </div>
                </HStack>
              </VStack>
              {validationError && (
                <Box className="text-sm text-red-600">{validationError}</Box>
              )}
            </VStack>
            <HStack className="-mx-6 -mb-5 justify-between gap-3 border-t border-borderColor px-6 py-4">
              <ButtonPrimary1 type="button" onClick={closeModal}>
                Cancel
              </ButtonPrimary1>
              <ButtonPrimary type="button" onClick={saveTag}>
                Save profile
              </ButtonPrimary>
            </HStack>
          </VStack>
        </VStack>
      </Dialog>
    </VStack>
  );
}

function EmployeePortalSettings({ openCategory }: { openCategory?: string }) {
  const {
    data: employeePortalSettingDetails,
    refetch,
    isError,
    isFetched,
  } = useGetEmployeePortalSettings();
  const { permission } = usePermissionStore();
  const isInternalUser = useIsInternalUser();

  const initialValues: EmployeePortalSetting = {
    showPlanToEmployees:
      employeePortalSettingDetails?.showPlanToEmployees || false,
    showEmpPortalValuesForecast:
      employeePortalSettingDetails?.showEmpPortalValuesForecast || false,
    showPPSToEmployees:
      employeePortalSettingDetails?.showPPSToEmployees || false,
    isNomineeMandatory:
      (employeePortalSettingDetails?.isNomineeMandatory as boolean) || false,
    mfaMandatoryForEmployee:
      (employeePortalSettingDetails?.mfaMandatoryForEmployee as boolean) ||
      false,
    showAtGrantPPSToEmployees:
      (employeePortalSettingDetails?.showAtGrantPPSToEmployees as boolean) ||
      false,
    enableGrantLetterDownload:
      (employeePortalSettingDetails?.enableGrantLetterDownload as boolean) ||
      false,
    allowEmployeeStatementDownload:
      employeePortalSettingDetails?.allowEmployeeStatementDownload ?? false,
    allowEmployeeChatbotAccess:
      employeePortalSettingDetails?.allowEmployeeChatbotAccess ?? false,
    showEquivalentShares:
      (employeePortalSettingDetails?.showEquivalentShares as boolean) ?? true,
    showFractionalPartOfEquivalentShares:
      (employeePortalSettingDetails?.showFractionalPartOfEquivalentShares as boolean) ||
      false,
    showPlanConversionRatio:
      (employeePortalSettingDetails?.showPlanConversionRatio as boolean) ??
      true,
    authMethods: employeePortalSettingDetails?.authMethods || [],
    generateCredentialsViaLink:
      employeePortalSettingDetails?.generateCredentialsViaLink || false,
    allowEmployeesToDownloadPlanDocuments:
      employeePortalSettingDetails?.allowEmployeesToDownloadPlanDocuments ??
      false,
    allowEmployeeToDownloadSettlementLetter:
      employeePortalSettingDetails?.allowEmployeeToDownloadSettlementLetter ??
      false,
    showTransactionsWhenEmpty:
      employeePortalSettingDetails?.showTransactionsWhenEmpty ?? true,
    hideExerciseValueForEmployees:
      employeePortalSettingDetails?.hideExerciseValueForEmployees || false,
    showRemainingOptionsCardOnDashboard:
      employeePortalSettingDetails?.showRemainingOptionsCardOnDashboard ?? true,
    showOptionsCardOnDashboard:
      employeePortalSettingDetails?.showOptionsCardOnDashboard ?? true,
    showRealizedCardOnDashboard:
      employeePortalSettingDetails?.showRealizedCardOnDashboard ?? true,
    showUnrealizedCardOnDashboard:
      employeePortalSettingDetails?.showUnrealizedCardOnDashboard ?? true,
    showLossCardOnDashboard:
      employeePortalSettingDetails?.showLossCardOnDashboard ?? true,
    showPercentagesInEmployeePortalTransactions:
      employeePortalSettingDetails?.showPercentagesInEmployeePortalTransactions ??
      true,
    showVestingTriggerType:
      employeePortalSettingDetails?.showVestingTriggerType ?? false,
    showNetGrantValue: employeePortalSettingDetails?.showNetGrantValue ?? false,
    showWealthInGrantSummary:
      employeePortalSettingDetails?.showWealthInGrantSummary ?? false,
    showOptionsExpiryDateToEmployees:
      employeePortalSettingDetails?.showOptionsExpiryDateToEmployees || false,
    percentagePrecision: (employeePortalSettingDetails?.percentagePrecision ??
      4) as 2 | 3 | 4,
    requireEmployeeNominationSignature:
      employeePortalSettingDetails?.requireEmployeeNominationSignature || false,
    nomineeFormTemplates:
      employeePortalSettingDetails?.nomineeFormTemplates || null,
    grantTagCatalog: employeePortalSettingDetails?.grantTagCatalog || [],
  };
  const validationSchema = Yup.object({
    showPPSToEmployees: Yup.boolean(),
    showPlanToEmployees: Yup.boolean(),
    showEmpPortalValuesForecast: Yup.boolean(),
    isNomineeMandatory: Yup.boolean(),
    mfaMandatoryForEmployee: Yup.boolean(),
    enableGrantLetterDownload: Yup.boolean(),
    allowEmployeeStatementDownload: Yup.boolean(),
    allowEmployeeChatbotAccess: Yup.boolean(),
    showEquivalentShares: Yup.boolean(),
    showFractionalPartOfEquivalentShares: Yup.boolean(),
    authMethods: Yup.array(),
    generateCredentialsViaLink: Yup.boolean(),
    allowEmployeesToDownloadPlanDocuments: Yup.boolean(),
    allowEmployeeToDownloadSettlementLetter: Yup.boolean(),
    showTransactionsWhenEmpty: Yup.boolean(),
    hideExerciseValueForEmployees: Yup.boolean(),
    showRemainingOptionsCardOnDashboard: Yup.boolean(),
    showOptionsCardOnDashboard: Yup.boolean(),
    showRealizedCardOnDashboard: Yup.boolean(),
    showUnrealizedCardOnDashboard: Yup.boolean(),
    showLossCardOnDashboard: Yup.boolean(),
    showPercentagesInEmployeePortalTransactions: Yup.boolean(),
    showVestingTriggerType: Yup.boolean(),
    showNetGrantValue: Yup.boolean(),
    showWealthInGrantSummary: Yup.boolean(),
    percentagePrecision: Yup.number().oneOf([2, 3, 4]),
    showOptionsExpiryDateToEmployees: Yup.boolean(),
    requireEmployeeNominationSignature: Yup.boolean(),
    nomineeFormTemplates: Yup.array()
      .of(Yup.object())
      .nullable()
      .when("requireEmployeeNominationSignature", {
        is: true,
        then: (schema) =>
          schema
            .min(1, "Nominee form template is required")
            .required("Nominee form template is required"),
        otherwise: (schema) => schema.nullable(),
      }),
  });

  const downloadNomineeFormTemplate = async (
    s3Path: string,
    fileName = "NomineeFormTemplate.docx"
  ) => {
    const res = await fetch(s3Path);
    const blob = await res.blob();
    downloadBlobObject(blob, fileName);
  };
  const { data: valuationDetails } = useValuationDetails();
  const { data: grantSharePriceData } = useGetGrantPPS(
    new Date().toISOString().slice(0, 10)
  );
  const { companyData } = useCompanyStore();
  const isPublicListed =
    companyData?.companyMetaData?.typeOfTheCompany === "PUBLIC";
  const { mutate: setEmployeePortalSettings, isLoading: isSettingsSaved } =
    useSetEmployeePortalSettings();
  function validateValuationDetailsForPPSUpdate(
    changedValues: Partial<EmployeePortalSetting>,
    valuationDetails: any[]
  ) {
    if (changedValues.showPPSToEmployees) {
      if (isPublicListed && !grantSharePriceData?.sharePriceOnGrantDate) {
        toast("Please sync share price before enabling PPS settings.", {
          type: "error",
          autoClose: 3000,
        });
        return false;
      }
      if (
        !isPublicListed &&
        (!valuationDetails ||
          valuationDetails.filter((val) => val.valuationSource === "COMPANY")
            .length === 0)
      ) {
        toast(
          "Please add at least one valuation before enabling PPS settings.",
          { type: "error", autoClose: 3000 }
        );
        return false;
      }
    }
    return true;
  }
  const handleSubmit = async (values: EmployeePortalSetting) => {
    const submitValues: EmployeePortalSetting = { ...values };
    if (
      submitValues.requireEmployeeNominationSignature &&
      !nomineePendingFile &&
      !hasNomineeTemplate(submitValues.nomineeFormTemplates)
    ) {
      toast("Please upload a nominee form template", { type: "error" });
      return;
    }
    if (nomineePendingFile) {
      try {
        const base64 = (await convertFileToBase64(
          nomineePendingFile
        )) as string;
        const cleaned = base64
          .toString()
          .replace("data:", "")
          .replace(/^.+,/, "");
        const templates = submitValues.nomineeFormTemplates
          ? [...submitValues.nomineeFormTemplates]
          : [];
        const userIndex = templates.findIndex(
          (template) => !template.isDefault && !template.isDeleted
        );
        const updatedUserTemplate: NomineeFormTemplate = {
          ...(userIndex >= 0 ? templates[userIndex] : {}),
          fileBlob: cleaned,
          fileName: nomineePendingFile.name,
          fileType: nomineePendingFile.type,
          isChanged: true,
          isDeleted: false,
          isAdded: true,
          isDefault: false,
        };
        if (userIndex >= 0) {
          templates[userIndex] = updatedUserTemplate;
        } else {
          templates.push(updatedUserTemplate);
        }
        submitValues.nomineeFormTemplates = templates;
      } catch (e) {
        toast("Failed to read attached file", { type: "error" });
        return;
      }
    }

    const changedValues: any = {};
    Object.keys(submitValues).forEach((key) => {
      const originalKey: keyof EmployeePortalSetting =
        key as keyof EmployeePortalSetting;
      if (submitValues[originalKey] !== initialValues[originalKey]) {
        changedValues[originalKey] = submitValues[originalKey];
      }
    });

    if (
      !validateValuationDetailsForPPSUpdate(
        changedValues,
        valuationDetails || []
      )
    ) {
      return;
    }

    if (Object.keys(changedValues).length === 0) {
      toast("No changes made", { type: "error", autoClose: 2000 });
      return;
    }

    setEmployeePortalSettings(changedValues, {
      onSuccess: () => {
        toast("Saved Successfully!", {
          type: "success",
          autoClose: 2000,
        });
        setNomineePendingFile(null);
        setTimeout(() => refetch && refetch(), 1000);
      },
      onError: (err: any) => {
        toast(err?.response?.data?.errorRaw || "Error occurred", {
          type: "error",
          autoClose: 2000,
        });
      },
    });
  };

  const getAuthMethodLabel = (authMethod: string) => {
    for (const [key, value] of Object.entries(AuthMethods)) {
      if (value === authMethod) return key;
    }
    return "";
  };

  const initialSettingsSet = useRef(false);
  const [initialSettings, setInitialSettings] = useState<EmployeePortalSetting>(
    employeePortalSettingDetails!
  );

  const formik = useFormik<EmployeePortalSetting>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: async (values) => {
      await handleSubmit(values);
    },
    onReset: (values) => {
      handleOnCancel(values);
    },
  });

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (
      formik.values.requireEmployeeNominationSignature &&
      !nomineePendingFile &&
      !hasNomineeTemplate(formik.values.nomineeFormTemplates)
    ) {
      toast("Please upload a nominee form template", { type: "error" });
      formik.setFieldTouched("nomineeFormTemplates", true, true);
      return;
    }
    await formik.submitForm();
  };

  const { values, errors, touched, getFieldProps, setFieldValue } = formik;
  useEffect(() => {
    if (employeePortalSettingDetails) {
      formik.setValues(employeePortalSettingDetails);
    }
    if (!initialSettingsSet.current) {
      setInitialSettings(_.cloneDeep(values));
      initialSettingsSet.current = true;
    }
  }, [employeePortalSettingDetails]);

  const [handleChanges, setHandleChanges] = useState<boolean>();

  useEffect(() => {
    if (openCategory) {
      // no tab construct here, but we could focus or toggle based on category if needed
      // For consistency, nothing to set specifically here for now.
    }
  }, [openCategory]);

  useEffect(() => {
    const hasChanges = _.isEqual(employeePortalSettingDetails, formik.values);

    setHandleChanges(hasChanges);
  }, [initialSettings, values, formik.values]);

  function handleOnCancel(values: EmployeePortalSetting) {
    values = initialValues;
  }

  const isUserEsopViewer = isEsopViewer();
  const currencySymbol = getCurrencySymbol();

  const employeePortalErrors =
    errors.showPPSToEmployees ||
    errors.showPlanToEmployees ||
    errors.showEmpPortalValuesForecast;
  function doPartialTouch() {
    formik.setFieldTouched("showPPSToEmployees");
    formik.setFieldTouched("showAtGrantPPSToEmployees");
    formik.setFieldTouched("showPlanToEmployees");
    formik.setFieldTouched("showEmpPortalValuesForecast");
    formik.setFieldTouched("enableGrantLetterDownload");
    formik.setFieldTouched("allowEmployeeStatementDownload");
    formik.setFieldTouched("allowEmployeeChatbotAccess");
    formik.setFieldTouched("showEquivalentShares");
    formik.setFieldTouched("showFractionalPartOfEquivalentShares");
    formik.setFieldTouched("authMethods");
    formik.setFieldTouched("generateCredentialsViaLink");
    formik.setFieldTouched("allowEmployeesToDownloadPlanDocuments");
    formik.setFieldTouched("allowEmployeeToDownloadSettlementLetter");
    formik.setFieldTouched("showTransactionsWhenEmpty");
    formik.setFieldTouched("hideExerciseValueForEmployees");
    formik.setFieldTouched("showRemainingOptionsCardOnDashboard");
    formik.setFieldTouched("showOptionsCardOnDashboard");
    formik.setFieldTouched("showRealizedCardOnDashboard");
    formik.setFieldTouched("showUnrealizedCardOnDashboard");
    formik.setFieldTouched("showLossCardOnDashboard");
    formik.setFieldTouched("showPercentagesInEmployeePortalTransactions");
    formik.setFieldTouched("showVestingTriggerType");
    formik.setFieldTouched("percentagePrecision");
    formik.setFieldTouched("requireEmployeeNominationSignature");
    formik.setFieldTouched("nomineeFormTemplates");
  }

  const [nomineeFileBase64, setNomineeFileBase64] = useState<string | null>(
    null
  );
  const [nomineeFileName, setNomineeFileName] = useState<string | null>(null);
  const [nomineePendingFile, setNomineePendingFile] = useState<File | null>(
    null
  );

  const hasNomineeTemplate = (
    templates: NomineeFormTemplate[] | null | undefined
  ) =>
    (templates || []).some(
      (template) => template?.fileName && !template.isDeleted
    );

  const getDefaultNomineeTemplate = (
    templates: NomineeFormTemplate[] | null | undefined
  ) => (templates || []).find((template) => template.isDefault);

  const getUserNomineeTemplate = (
    templates: NomineeFormTemplate[] | null | undefined
  ) =>
    (templates || []).find(
      (template) => !template.isDefault && !template.isDeleted
    );

  const displayNomineeTemplate = useMemo(
    () =>
      getUserNomineeTemplate(formik.values.nomineeFormTemplates) ||
      getDefaultNomineeTemplate(formik.values.nomineeFormTemplates) ||
      null,
    [formik.values.nomineeFormTemplates]
  );

  const userNomineeTemplate = useMemo(
    () => getUserNomineeTemplate(formik.values.nomineeFormTemplates),
    [formik.values.nomineeFormTemplates]
  );

  const nomineeFileInputRef = useRef<HTMLInputElement | null>(null);

  async function handleNomineeFileUpload(
    e: React.ChangeEvent<HTMLInputElement>
  ) {
    if (!e?.target?.files?.[0]) return;
    const file = e.target.files[0];
    const allowed = [
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/msword",
    ];
    if (
      !allowed.includes(file.type) &&
      !file.name.endsWith(".docx") &&
      !file.name.endsWith(".doc")
    ) {
      toast("Please upload a .docx or .doc file", { type: "error" });
      e.target.value = "";
      return;
    }
    setNomineePendingFile(file);
    const currentTemplates = formik.values.nomineeFormTemplates || [];
    const defaultTemplates = currentTemplates.filter(
      (template) => template.isDefault
    );
    const deletedUserTemplates = currentTemplates.filter(
      (template) => !template.isDefault && template.isDeleted
    );
    const existingUserTemplate = getUserNomineeTemplate(currentTemplates);
    const newUserTemplate: NomineeFormTemplate = {
      ...(existingUserTemplate || {}),
      templateId: existingUserTemplate?.templateId,
      fileName: file.name,
      fileType: file.type,
      isChanged: true,
      isDeleted: false,
      isAdded: true,
      isDefault: false,
    };
    formik.setFieldValue("nomineeFormTemplates", [
      ...defaultTemplates,
      newUserTemplate,
      ...deletedUserTemplates,
    ]);
    toast("File attached (will be saved on Save Settings)", {
      type: "info",
      autoClose: 1200,
    });
    e.target.value = "";
  }

  return (
    <form key="employeePortalSettings" onSubmit={handleFormSubmit}>
      <Box className="md:w-[425px] lg:w-[850px] p-4 bg-white rounded-lg">
        <Box className="">
          <VStack className="justify-between p-4 gap-9">
            <Box className="items-center mt-6 text-lg font-medium text-gray-dark">
              <SettingHeader
                title="Employee Portal Settings"
                description="Configure your employee portal settings"
              />
            </Box>
            <HStack className="gap-8 mb-0 border-t-2 border-dotted flex-center"></HStack>
            <VStack className="p-4">
              <HStack className="gap-10 pb-6">
                <VStack className="w-full gap-10">
                  <SettingsRow
                    id="portal-showPPSToEmployees"
                    label="Display Current Value on Employee portal"
                    description="Enable this option to show current values on the employee portal."
                    name="showPPSToEmployees"
                    type="toggle"
                    value={formik.values.showPPSToEmployees || false}
                    onToggle={() => {
                      formik.setFieldValue(
                        "showPPSToEmployees",
                        !values.showPPSToEmployees
                      );
                    }}
                  />
                  <VStack
                    className={`justify-between gap-5 pl-10 ${
                      formik.values.showPPSToEmployees
                        ? ""
                        : "opacity-50 cursor-not-allowed"
                    }`}
                  >
                    <SettingsRow
                      id="portal-showAtGrantPPSToEmployees"
                      label="Display Value at grant on Employee portal"
                      description="Enable this option to show grant values on the employee portal."
                      name="showAtGrantPPSToEmployees"
                      type="toggle"
                      value={formik.values.showAtGrantPPSToEmployees || false}
                      onToggle={() => {
                        formik.setFieldValue(
                          "showAtGrantPPSToEmployees",
                          !values.showAtGrantPPSToEmployees
                        );
                      }}
                      disabled={!formik.values.showPPSToEmployees}
                    />
                    <SettingsRow
                      id="portal-hideExerciseValueForEmployees"
                      label="Exclude share values from the Employee Dashboard valuation graph"
                      description="Employee dashboard graph displays valuation excluding all share-related values"
                      name="hideExerciseValueForEmployees"
                      type="toggle"
                      value={
                        formik.values.hideExerciseValueForEmployees || false
                      }
                      onToggle={() => {
                        formik.setFieldValue(
                          "hideExerciseValueForEmployees",
                          !values.hideExerciseValueForEmployees
                        );
                      }}
                      disabled={!formik.values.showPPSToEmployees}
                    />
                  </VStack>
                  <SettingsRow
                    id="portal-showOptionsCardOnDashboard"
                    label="Display Total Options card on Employee Dashboard"
                    description="Enable this option to show the total options card on the employee dashboard."
                    name="showOptionsCardOnDashboard"
                    type="toggle"
                    value={formik.values.showOptionsCardOnDashboard ?? true}
                    onToggle={() => {
                      formik.setFieldValue(
                        "showOptionsCardOnDashboard",
                        !values.showOptionsCardOnDashboard
                      );
                    }}
                  />
                  <SettingsRow
                    id="portal-showRemainingOptionsCardOnDashboard"
                    label="Display Remaining Options card on Employee Dashboard"
                    description="Enable this option to show the remaining options card on the employee dashboard."
                    name="showRemainingOptionsCardOnDashboard"
                    type="toggle"
                    value={
                      formik.values.showRemainingOptionsCardOnDashboard ?? true
                    }
                    onToggle={() => {
                      formik.setFieldValue(
                        "showRemainingOptionsCardOnDashboard",
                        !values.showRemainingOptionsCardOnDashboard
                      );
                    }}
                  />
                  <SettingsRow
                    id="portal-showRealizedCardOnDashboard"
                    label="Display Realized card on Employee Dashboard"
                    description="Enable this option to show the realized value or options card on the employee dashboard."
                    name="showRealizedCardOnDashboard"
                    type="toggle"
                    value={formik.values.showRealizedCardOnDashboard ?? true}
                    onToggle={() => {
                      formik.setFieldValue(
                        "showRealizedCardOnDashboard",
                        !values.showRealizedCardOnDashboard
                      );
                    }}
                  />
                  <SettingsRow
                    id="portal-showUnrealizedCardOnDashboard"
                    label="Display Unrealized card on Employee Dashboard"
                    description="Enable this option to show the unrealized value or options card on the employee dashboard."
                    name="showUnrealizedCardOnDashboard"
                    type="toggle"
                    value={formik.values.showUnrealizedCardOnDashboard ?? true}
                    onToggle={() => {
                      formik.setFieldValue(
                        "showUnrealizedCardOnDashboard",
                        !values.showUnrealizedCardOnDashboard
                      );
                    }}
                  />
                  <SettingsRow
                    id="portal-showLossCardOnDashboard"
                    label="Display Loss card on Employee Dashboard"
                    description="Enable this option to show the loss value or options card on the employee dashboard."
                    name="showLossCardOnDashboard"
                    type="toggle"
                    value={formik.values.showLossCardOnDashboard ?? true}
                    onToggle={() => {
                      formik.setFieldValue(
                        "showLossCardOnDashboard",
                        !values.showLossCardOnDashboard
                      );
                    }}
                  />
                  <SettingsRow
                    id="portal-showPercentagesInEmployeePortalTransactions"
                    label="Display Percentages in Employee Portal Transactions"
                    description="Enable this option to show percentage values in transaction details on the employee portal."
                    name="showPercentagesInEmployeePortalTransactions"
                    type="toggle"
                    value={
                      formik.values
                        .showPercentagesInEmployeePortalTransactions ?? true
                    }
                    onToggle={() => {
                      formik.setFieldValue(
                        "showPercentagesInEmployeePortalTransactions",
                        !values.showPercentagesInEmployeePortalTransactions
                      );
                    }}
                  />
                  <SettingsRow
                    id="portal-percentagePrecision"
                    label="Percentage Decimal Precision"
                    description="Number of decimal places shown for vesting percentages in the employee portal (values are truncated, not rounded)."
                    name="percentagePrecision"
                    type="select"
                    value={formik.values.percentagePrecision ?? 4}
                    options={[
                      { label: "2 decimals", value: 2 },
                      { label: "3 decimals", value: 3 },
                      { label: "4 decimals", value: 4 },
                    ]}
                    textGetter={(o: any) => o.label}
                    valueGetter={(o: any) => o.value}
                    onChange={([val]: any[]) =>
                      formik.setFieldValue("percentagePrecision", Number(val))
                    }
                  />
                  <SettingsRow
                    id="portal-mfaMandatoryForEmployee"
                    label="Mandate Employee MFA"
                    description="Enabling this will enforce multi-factor authentication for all employees by overriding their employee portal settings, except for users who log in via Google and Microsoft."
                    name="mfaMandatoryForEmployee"
                    type="toggle"
                    value={formik.values.mfaMandatoryForEmployee || false}
                    onToggle={() => {
                      formik.setFieldValue(
                        "mfaMandatoryForEmployee",
                        !values.mfaMandatoryForEmployee
                      );
                    }}
                  />
                  <SettingsRow
                    id="portal-showEmpPortalValuesForecast"
                    label="Display forecast values in Employee portal"
                    description="Enable this option to show projected values of grants."
                    name="showEmpPortalValuesForecast"
                    type="toggle"
                    value={formik.values.showEmpPortalValuesForecast || false}
                    onToggle={() => {
                      formik.setFieldValue(
                        "showEmpPortalValuesForecast",
                        !values.showEmpPortalValuesForecast
                      );
                    }}
                  />
                  <SettingsRow
                    id="portal-showTransactionsWhenEmpty"
                    label="Show transaction(s) menu on employee portal"
                    description="Enable this option to display the transactions menu even if no transactions exist."
                    name="showTransactionsWhenEmpty"
                    type="toggle"
                    value={formik.values.showTransactionsWhenEmpty || false}
                    onToggle={() =>
                      formik.setFieldValue(
                        "showTransactionsWhenEmpty",
                        !formik.values.showTransactionsWhenEmpty
                      )
                    }
                  />
                  <SettingsRow
                    id="portal-showPlanToEmployees"
                    label="Show Plan to Employee"
                    description="Enable this option to allow employees to view their plan details."
                    name="showPlanToEmployees"
                    type="toggle"
                    value={formik.values.showPlanToEmployees || false}
                    onToggle={() =>
                      formik.setFieldValue(
                        "showPlanToEmployees",
                        !formik.values.showPlanToEmployees
                      )
                    }
                  />
                  <VStack className="justify-between gap-5 pl-10">
                    <SettingsRow
                      id="portal-allowEmployeesToDownloadPlanDocuments"
                      label="Allow Employee to Download Plan Documents"
                      description="Enable this option to let employees download plan-related documents."
                      name="allowEmployeesToDownloadPlanDocuments"
                      type="toggle"
                      disabled={!formik.values.showPlanToEmployees}
                      value={
                        formik.values.allowEmployeesToDownloadPlanDocuments ||
                        false
                      }
                      onToggle={() => {
                        formik.setFieldValue(
                          "allowEmployeesToDownloadPlanDocuments",
                          !values.allowEmployeesToDownloadPlanDocuments
                        );
                      }}
                    />
                  </VStack>
                  <SettingsRow
                    id="portal-allowEmployeeToDownloadSettlementLetter"
                    label="Allow Employee to Download F&F Settlement Letter"
                    description="Enable this option to let employees download their Full & Final Settlement letters."
                    name="allowEmployeeToDownloadSettlementLetter"
                    type="toggle"
                    value={
                      formik.values.allowEmployeeToDownloadSettlementLetter ||
                      false
                    }
                    onToggle={() => {
                      formik.setFieldValue(
                        "allowEmployeeToDownloadSettlementLetter",
                        !values.allowEmployeeToDownloadSettlementLetter
                      );
                    }}
                  />
                  <SettingsRow
                    id="portal-showOptionsExpiryDateToEmployees"
                    label="Display Options Expiry Date in Employee portal"
                    description="Enable this option to display option expiry date for a grant in the employee portal for all the employees."
                    name="showOptionsExpiryDateToEmployees"
                    type="toggle"
                    value={
                      formik.values.showOptionsExpiryDateToEmployees || false
                    }
                    onToggle={() => {
                      formik.setFieldValue(
                        "showOptionsExpiryDateToEmployees",
                        !formik.values.showOptionsExpiryDateToEmployees
                      );
                    }}
                  />

                  <SettingsRow
                    id="portal-isNomineeMandatory"
                    label="Mandatory Nominee Information for Employees"
                    description="Enable this option to make nominee information mandatory for employees."
                    name="isNomineeMandatory"
                    type="toggle"
                    value={formik.values.isNomineeMandatory || false}
                    onToggle={() =>
                      formik.setFieldValue(
                        "isNomineeMandatory",
                        !formik.values.isNomineeMandatory
                      )
                    }
                  />

                  <SettingsRow
                    id="portal-requireEmployeeNominationSignature"
                    label="Require Employee Signature on Nominee Form"
                    description="If enabled, employees will be required to sign the nominee form. Upload a nominee form template (DOCX) below."
                    name="requireEmployeeNominationSignature"
                    type="toggle"
                    value={
                      formik.values.requireEmployeeNominationSignature || false
                    }
                    onToggle={() =>
                      formik.setFieldValue(
                        "requireEmployeeNominationSignature",
                        !formik.values.requireEmployeeNominationSignature
                      )
                    }
                  />

                  {formik.values.requireEmployeeNominationSignature && (
                    <VStack className="justify-between gap-5 pl-10">
                      <Box className="max-h-full overflow-auto">
                        <table className="w-full table-space">
                          <thead className="text-xs font-medium text-[#B5B5C3] ">
                            <tr className=" border-b border-[#E4E6EF] border-dashed">
                              <td className="py-3 hover:cursor-pointer">
                                <HStack className="ml-2">
                                  FILE<VStack className="h-4"></VStack>
                                </HStack>
                              </td>
                              <td className="py-3 hover:cursor-pointer">
                                <HStack>
                                  ATTACHED FILE
                                  <VStack className="h-4"></VStack>
                                </HStack>
                              </td>
                              <td className="py-3 hover:cursor-pointer">
                                <HStack>
                                  UPLOAD
                                  <VStack className="h-4"></VStack>
                                </HStack>
                              </td>
                              <td className="py-3 hover:cursor-pointer">
                                <HStack>
                                  DELETE
                                  <VStack className="h-4"></VStack>
                                </HStack>
                              </td>
                            </tr>
                          </thead>
                          <tbody className="font-medium text-dark">
                            <tr className=" border-b border-[#E4E6EF] border-dashed cursor-pointer hover:bg-slate-50">
                              <td className="py-2">
                                <Box className="w-40 mt-4">Nominee Form</Box>
                              </td>
                              <td className="py-2">
                                <Box className="w-56">
                                  {displayNomineeTemplate?.fileName ? (
                                    <div
                                      className="mt-4 text-red-500 underline"
                                      onClick={() =>
                                        downloadNomineeFormTemplate(
                                          displayNomineeTemplate?.s3Path as string,
                                          `${displayNomineeTemplate?.fileName}-NomineeFormTemplate.docx`
                                        )
                                      }
                                    >
                                      {displayNomineeTemplate?.fileName}
                                    </div>
                                  ) : (
                                    <div className="mt-4 text-gray-400">
                                      No file attached
                                    </div>
                                  )}
                                </Box>
                              </td>
                              <td className="py-2">
                                <Box className="w-8 mt-4">
                                  <input
                                    ref={nomineeFileInputRef}
                                    type="file"
                                    accept=".docx,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
                                    onChange={handleNomineeFileUpload}
                                    style={{ display: "none" }}
                                  />
                                  <label
                                    className="px-2 py-2 rounded-lg cursor-pointer "
                                    onClick={() =>
                                      nomineeFileInputRef.current?.click()
                                    }
                                  >
                                    <Icon
                                      icon="ic:baseline-file-upload"
                                      height={24}
                                    />
                                  </label>
                                </Box>
                              </td>
                              <td className="py-2">
                                {userNomineeTemplate && (
                                  <Box className="w-8 mt-4 ml-2">
                                    <div
                                      onClick={() => {
                                        const currentTemplates =
                                          formik.values.nomineeFormTemplates ||
                                          [];
                                        const currentUserTemplate =
                                          getUserNomineeTemplate(
                                            currentTemplates
                                          );
                                        if (!currentUserTemplate) {
                                          return;
                                        }
                                        const defaultTemplates =
                                          currentTemplates.filter(
                                            (template) => template.isDefault
                                          );
                                        const deletedTemplates =
                                          currentTemplates.filter(
                                            (template) =>
                                              !template.isDefault &&
                                              template.isDeleted
                                          );
                                        const updatedTemplates =
                                          currentUserTemplate.templateId
                                            ? [
                                                ...defaultTemplates,
                                                {
                                                  ...currentUserTemplate,
                                                  isDeleted: true,
                                                  isChanged: true,
                                                  isAdded: false,
                                                },
                                                ...deletedTemplates,
                                              ]
                                            : [
                                                ...defaultTemplates,
                                                ...deletedTemplates,
                                              ];
                                        formik.setFieldValue(
                                          "nomineeFormTemplates",
                                          updatedTemplates
                                        );
                                        setNomineeFileBase64("");
                                        setNomineeFileName("Upload File");
                                        setNomineePendingFile(null);
                                        if (nomineeFileInputRef.current) {
                                          nomineeFileInputRef.current.value =
                                            "";
                                        }
                                      }}
                                      className={`${
                                        displayNomineeTemplate?.fileName
                                          ? "text-zinc-300 hover:scale-105"
                                          : "text-zinc-200 cursor-not-allowed"
                                      }`}
                                    >
                                      <Icon
                                        icon="mi:delete"
                                        color="#ff0000"
                                        height={24}
                                      />
                                    </div>
                                  </Box>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </Box>
                    </VStack>
                  )}

                  <SettingsRow
                    id="portal-enableGrantLetterDownload"
                    label="Allow Employee to Download Grant Letter"
                    description="Enable this option to let employees download their grant letters."
                    name="enableGrantLetterDownload"
                    type="toggle"
                    value={formik.values.enableGrantLetterDownload || false}
                    onToggle={() =>
                      formik.setFieldValue(
                        "enableGrantLetterDownload",
                        !formik.values.enableGrantLetterDownload
                      )
                    }
                  />

                  <SettingsRow
                    id="portal-allowEmployeeStatementDownload"
                    label="Allow Employees to Download ESOP Statement"
                    description="When enabled, employees can generate and download their ESOP statement as a PDF from the portal."
                    name="allowEmployeeStatementDownload"
                    type="toggle"
                    value={
                      formik.values.allowEmployeeStatementDownload || false
                    }
                    onToggle={() =>
                      formik.setFieldValue(
                        "allowEmployeeStatementDownload",
                        !formik.values.allowEmployeeStatementDownload
                      )
                    }
                  />

                  {isInternalUser && (
                    <SettingsRow
                      id="portal-allowEmployeeChatbotAccess"
                      label="Allow Employees to Use Chatbot"
                      description="When enabled, employees can register for and use the ESOP chatbot to view grant details, set a security PIN, and get tax estimates."
                      name="allowEmployeeChatbotAccess"
                      type="toggle"
                      value={formik.values.allowEmployeeChatbotAccess || false}
                      onToggle={() =>
                        formik.setFieldValue(
                          "allowEmployeeChatbotAccess",
                          !formik.values.allowEmployeeChatbotAccess
                        )
                      }
                    />
                  )}

                  <SettingsRow
                    id="portal-showEquivalentShares"
                    label="Show Equivalent Shares (Options to shares conversion)"
                    description="Enabling this option will display the equivalent shares for the options in both the Remaining Options card and the Grant card."
                    name="showEquivalentShares"
                    type="toggle"
                    value={formik.values.showEquivalentShares ?? true}
                    onToggle={() => {
                      formik.setFieldValue(
                        "showEquivalentShares",
                        !formik.values.showEquivalentShares
                      );
                    }}
                  />

                  <VStack
                    className={`justify-between gap-5 pl-10 ${
                      formik.values.showEquivalentShares
                        ? ""
                        : "opacity-50 cursor-not-allowed"
                    }`}
                  >
                    <SettingsRow
                      id="portal-showFractionalPartOfEquivalentShares"
                      label="Show Fractional Part of Equivalent Shares"
                      description="Enable this option to show decimal portions of converted shares."
                      name="showFractionalPartOfEquivalentShares"
                      type="toggle"
                      value={
                        formik.values.showFractionalPartOfEquivalentShares ||
                        false
                      }
                      onToggle={() =>
                        formik.setFieldValue(
                          "showFractionalPartOfEquivalentShares",
                          !formik.values.showFractionalPartOfEquivalentShares
                        )
                      }
                      disabled={!formik.values.showEquivalentShares}
                    />
                    <SettingsRow
                      id="portal-showPlanConversionRatio"
                      label="Show Plan Conversion Ratio to Employees"
                      description="Enable this option to show plan conversion ratios to employees."
                      name="showPlanConversionRatio"
                      type="toggle"
                      value={formik.values.showPlanConversionRatio}
                      onToggle={() =>
                        formik.setFieldValue(
                          "showPlanConversionRatio",
                          !formik.values.showPlanConversionRatio
                        )
                      }
                      disabled={!formik.values.showEquivalentShares}
                    />
                  </VStack>

                  <SettingsRow
                    id="portal-authMethods"
                    label="Allowed Sign-In Authentication Methods for Employees"
                    description="Choose the sign-in methods employees can use."
                    name="authMethods"
                    type="multiselect"
                    value={formik.values.authMethods.map((value) =>
                      getAuthMethodLabel(value)
                    )}
                    onChange={(values: string[]) => {
                      const authMethods = values.map(
                        (value) => AuthMethods[value]
                      );
                      if (!authMethods.includes("passwordBased"))
                        authMethods.push("passwordBased");
                      formik.setFieldValue("authMethods", authMethods);
                    }}
                    options={[
                      { value: "Password Based" },
                      { value: "Sign in with Google" },
                      { value: "Sign in with Microsoft" },
                    ]}
                    disabled={formik.values.authMethods.length === 0}
                  />

                  <SettingsRow
                    id="portal-generateCredentialsViaLink"
                    label="Generate Credentials for Employees via OTP"
                    description="Enable this option to send login credentials via OTP link."
                    name="generateCredentialsViaLink"
                    type="toggle"
                    value={formik.values.generateCredentialsViaLink || false}
                    onToggle={() =>
                      formik.setFieldValue(
                        "generateCredentialsViaLink",
                        !formik.values.generateCredentialsViaLink
                      )
                    }
                  />
                  <SettingsRow
                    id="portal-showVestingTriggerType"
                    label="Show Vesting Trigger Type"
                    description="Enable this option to show the vesting trigger type on the employee portal."
                    name="showVestingTriggerType"
                    type="toggle"
                    value={formik.values.showVestingTriggerType ?? false}
                    onToggle={() => {
                      formik.setFieldValue(
                        "showVestingTriggerType",
                        !formik.values.showVestingTriggerType
                      );
                    }}
                  />
                  <SettingsRow
                    id="portal-showNetGrantValue"
                    label="Show options value with strike price deducted"
                    description="Enable this option to show option values with the strike price already deducted on the employee portal."
                    name="showNetGrantValue"
                    type="toggle"
                    value={formik.values.showNetGrantValue ?? false}
                    onToggle={() => {
                      formik.setFieldValue(
                        "showNetGrantValue",
                        !formik.values.showNetGrantValue
                      );
                      if (!formik.values.showNetGrantValue) {
                        formik.setFieldValue("showWealthInGrantSummary", false);
                      }
                    }}
                  />
                  {!formik.values.showNetGrantValue && (
                    <SettingsRow
                      id="portal-showWealthInGrantSummary"
                      label="Show wealth in Grant Summary"
                      description={
                        formik.values.showNetGrantValue
                          ? "Disable strike price deduction to show Total Wealth alongside total value."
                        : "Enable this option to show Total Wealth alongside total value in the employee Grant Summary."
                    }
                    name="showWealthInGrantSummary"
                    type="toggle"
                    value={formik.values.showWealthInGrantSummary ?? false}
                    disabled={formik.values.showNetGrantValue ?? false}
                    onToggle={() => {
                      formik.setFieldValue(
                        "showWealthInGrantSummary",
                        !formik.values.showWealthInGrantSummary
                      );
                    }}
                  />)}
                </VStack>
              </HStack>
              <GrantTagCatalogSettings formik={formik} />
            </VStack>
          </VStack>
        </Box>
        {!isUserEsopViewer && (
          <HStack className="justify-between mt-12">
            <ButtonPrimary1 type="reset" className="mr-8 text-red-500">
              Cancel
            </ButtonPrimary1>
            <ButtonPrimary
              className={`${
                !handleChanges ||
                !determineUserAccessToResource(
                  permission?.aclList || [],
                  Resource.CompanySettings,
                  DefaultAction.All
                )
                  ? " cursor-not-allowed"
                  : ""
              } flex items-center self-end justify-center`}
              disabled={
                isError ||
                handleChanges ||
                !determineUserAccessToResource(
                  permission?.aclList || [],
                  Resource.CompanySettings,
                  DefaultAction.All
                )
              }
              type="submit"
              loading={isSettingsSaved}
            >
              Save Settings
            </ButtonPrimary>
          </HStack>
        )}
      </Box>
    </form>
  );
}

export default EmployeePortalSettings;
